// LivestreamPinnedMessages.jsx
import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import PinBlueIconSource from '../../img/ic_pin_livechat.svg';
import IconArrowUpSource from '../../img/ic_arrow_up.svg';
import defaults from '../resource/defaults.js';
import { textColor } from '../style/variables.js';

const COLLAPSED_HEIGHT = 36;
const EXPANDED_MAX_HEIGHT = 138;
const WRAPPER_PADDING_TOP = 6;

const getWrapperCss = ({ isExpanded }) => {
  if (isExpanded) {
    return css`
      height: auto;
      max-height: ${EXPANDED_MAX_HEIGHT}px;
    `;
  }
  return css`
    height: ${COLLAPSED_HEIGHT}px;
    max-height: ${COLLAPSED_HEIGHT}px;
  `;
};

const PinnedMessageItem = memo(({ message }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTextOverflowed, setIsTextOverflowed] = useState(false);
  const textRef = useRef(null);
  const toggleExpanded = useCallback(() => {
    if (isTextOverflowed) setIsExpanded(prev => !prev);
  }, [isTextOverflowed]);

  useEffect(() => {
    setIsExpanded(false);

    const timer = setTimeout(() => {
      if (textRef.current) {
        const { scrollWidth, clientWidth, scrollHeight, clientHeight } =
          textRef.current;
        setIsTextOverflowed(
          scrollWidth > clientWidth || scrollHeight > clientHeight
        );
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [message?.id]);

  return (
    <Wrapper
      isExpanded={isExpanded}
      isTextOverflowed={isTextOverflowed}
      onClick={toggleExpanded}
    >
      <IconsWrapper>
        <PinnedIcon>
          <img src={PinBlueIconSource} alt="pinned" />
        </PinnedIcon>
        {isTextOverflowed && (
          <ExpandIcon isExpanded={isExpanded}>
            <img src={IconArrowUpSource} alt="expand" />
          </ExpandIcon>
        )}
      </IconsWrapper>
      <TextWrapper>
        <PinnedMessage ref={textRef} isExpanded={isExpanded}>
          {message.text}
        </PinnedMessage>
      </TextWrapper>
    </Wrapper>
  );
});

PinnedMessageItem.displayName = 'PinnedMessageItem';

PinnedMessageItem.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
    timestamp: PropTypes.number,
  }).isRequired,
};

export const LivestreamPinnedMessages = ({
  pinnedMessages = defaults.EMPTY_ARRAY,
}) => {
  if (!pinnedMessages || !pinnedMessages.length) return null;

  return (
    <StyledLivestreamPinnedMessages className="swiper-no-swiping">
      {pinnedMessages.map(message => {
        if (!message) return null;
        return <PinnedMessageItem key={message.id} message={message} />;
      })}
    </StyledLivestreamPinnedMessages>
  );
};

LivestreamPinnedMessages.propTypes = {
  pinnedMessages: PropTypes.array,
};

const StyledLivestreamPinnedMessages = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Wrapper = styled.div`
  cursor: ${({ isTextOverflowed }) =>
    isTextOverflowed ? 'pointer' : 'default'};
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  ${getWrapperCss}
  padding-top: ${WRAPPER_PADDING_TOP}px;
  padding-right: 32px;
  padding-bottom: 6px;
  padding-left: 40px;
  border-radius: 4px;
  background-color: rgba(74, 144, 226, 0.6);
  overflow: hidden;
  transition:
    height 0.15s ease-in-out,
    max-height 0.15s ease-in-out,
    padding-top 0.1s ease-out;
`;

const IconsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 6px 8px;
  align-items: center;
`;

const PinnedIcon = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  > img {
    width: 24px;
    height: 100%;
    border-radius: 50%;
    background-color: #ffffff;
  }
`;

const TextWrapper = styled.div`
  position: relative;
  display: inline-flex;
  min-width: 0;
  flex: 1;
  z-index: 1;
`;

const PinnedMessage = styled.div`
  max-height: ${EXPANDED_MAX_HEIGHT - WRAPPER_PADDING_TOP}px;
  padding-top: 3px;
  padding-bottom: 6px;
  overflow: hidden auto;
  &::-webkit-scrollbar {
    display: none;
  }
  text-overflow: ellipsis;
  white-space: ${({ isExpanded }) => (isExpanded ? 'no-wrap' : 'pre')};
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  color: ${textColor.white};
  overflow-wrap: break-word;
`;

const ExpandIcon = styled.div`
  position: relative;
  width: 16px;
  height: 16px;
  > img {
    width: 16px;
    height: 100%;
    transform-origin: center;
    transform: ${({ isExpanded }) =>
      isExpanded ? 'rotate(0deg)' : 'rotate(180deg)'};
    transition: transform 0.15s ease-in-out;
  }
`;

export default LivestreamPinnedMessages;
